// Crew counts in and out of the clipboard (#589).
//
// "Similar to editing multiple cells on a spreadsheet" means, to a scheduler,
// that a block copied out of Excel pastes onto the grid. Excel puts plain
// tab-separated text on the clipboard, so that is the format both ways: a block
// copied off the grid pastes back into Excel too.
//
// ⚠️ PASTED TEXT IS UNTRUSTED INPUT, and it is validated here rather than left
// to the API. The draft DTO does cap crewCount at 0–999, but a bad value there
// fails the WHOLE batch with a 400 after the fact, and 999 is far beyond any
// real crew — a column of dollar figures or hours pasted by mistake would sail
// under it. So the paste is checked before anything is staged, and a single bad
// cell refuses the paste with its location named: a clipboard holding text
// where numbers should be is not the block the scheduler thinks it is, so
// writing "the good parts" of it would be guessing.

/**
 * The largest crew one job line can plausibly carry on one day.
 *
 * Above the company's whole default Available MP/day (144), so any real plan
 * fits, while a pasted dollar figure, hour total or job number does not. The
 * API's own ceiling (UpsertScheduleEntryDto @Max(999)) stays the backstop.
 */
export const MAX_CELL_CREW = 200;

/** Largest block parsed at all — far past any real paste, short of a runaway one. */
export const MAX_PASTE_CELLS = 5000;

export type CrewValue = { ok: true; value: number } | { ok: false; why: string };

/**
 * One typed or pasted value as a crew count.
 *
 * Empty is 0: pasting a blank over a spreadsheet cell clears it, and the grid
 * draws 0 as blank. A trailing `.0` is accepted because Excel copies a
 * one-decimal number format that way; anything genuinely fractional is not.
 */
export function parseCrewValue(raw: string): CrewValue {
  const s = raw.trim();
  if (s === '') return { ok: true, value: 0 };
  const m = /^(\d+)(?:\.0+)?$/.exec(s);
  if (!m) {
    if (/^-\s*\d/.test(s)) return { ok: false, why: 'crew can’t be negative' };
    if (/^\d*\.\d+$/.test(s)) return { ok: false, why: 'crew must be a whole number' };
    return { ok: false, why: 'not a crew count' };
  }
  const value = Number(m[1]);
  if (value > MAX_CELL_CREW) return { ok: false, why: `more than ${MAX_CELL_CREW} on one line in one day` };
  return { ok: true, value };
}

export interface PasteProblem {
  /** 1-based, as the reader would count them in their spreadsheet. */
  row: number;
  col: number;
  raw: string;
  why: string;
}

/**
 * Is this clipboard text a BLOCK (several cells) rather than one value?
 *
 * Decides whether a paste into a box that takes a single number (an open grid
 * cell, the bar's Set box) is redirected to the grid as a block.
 *
 * ⚠️ Not `trim()` then look for delimiters: blank cells ARE delimiters plus
 * nothing, so trimming `5\t\r\n` ([5, 0]) or `5\r\n\r\n` ([[5], [0]]) erased
 * the very tabs and newlines that made them blocks, and the paste fell into
 * the box as a bare "5" — the cells meant to be cleared never were (Codex on
 * #612). Exactly ONE trailing newline is ignored, because that is how Excel
 * terminates even a single copied cell (`5\r\n`), and one value should still
 * go into the box as typed text.
 */
export function isBlockPaste(text: string): boolean {
  return /[\t\n]/.test(clipboardBody(text));
}

/**
 * Clipboard text with its line endings normalized (CRLF, and a lone CR as
 * classic Mac sources write it, both become LF) and ONE trailing newline
 * dropped. The single reading isBlockPaste and parseClipboard share, so they
 * can never disagree about whether "5\r" is one value or a block.
 */
function clipboardBody(text: string): string {
  return text.replace(/\r\n?/g, '\n').replace(/\n$/, '');
}

export type ParsedPaste =
  | { ok: true; matrix: (number | null)[][] }
  | { ok: false; problems: PasteProblem[]; message: string };

/**
 * Tab-separated clipboard text → a matrix of crew counts.
 *
 * - Rows split on CRLF / LF; the single trailing newline Excel appends is
 *   dropped, so it doesn't read as an extra blank row that clears a line.
 * - A row shorter than the widest one is padded with null = "leave
 *   unchanged", not 0: a ragged row comes from a non-spreadsheet source, and
 *   clearing cells it never mentioned would be inventing intent.
 * - Every bad cell is collected (up to five reported) and the whole paste is
 *   refused.
 */
export function parseClipboard(text: string): ParsedPaste {
  // Truly empty, or stray spaces with no cell structure at all. A lone newline
  // or tabs are NOT empty: that is how Excel (and this grid) copies blank
  // cells, and pasting blanks clears, as in a spreadsheet.
  if (text.trim() === '' && !/[\t\r\n]/.test(text)) {
    return { ok: false, problems: [], message: 'The clipboard is empty — copy some cells first.' };
  }
  const body = clipboardBody(text);
  const lines = body.split('\n').map((l) => l.split('\t'));
  // ⚠️ Bound the PADDED size — rows × widest row — not the cells actually
  // delimited. Ragged rows are padded out to the widest one below, so 2,500
  // one-cell rows plus one 2,500-cell row is only 5,000 cells as written but
  // ~6.25 million slots once padded, allocated on the UI thread mid-paste
  // (Codex on #612). Width by reduce, not Math.max(...): spreading a very
  // long clipboard's rows as arguments can itself overflow the call stack.
  const width = lines.reduce((w, r) => Math.max(w, r.length), 0);
  const size = lines.length * width;
  if (size > MAX_PASTE_CELLS) {
    return {
      ok: false, problems: [],
      message: `That’s a block of ${lines.length.toLocaleString()} rows × ${width.toLocaleString()} columns — far more than the grid shows. Nothing was pasted.`,
    };
  }
  const problems: PasteProblem[] = [];
  const matrix = lines.map((r, i) => {
    const out: (number | null)[] = r.map((raw, j) => {
      const v = parseCrewValue(raw);
      if (v.ok) return v.value;
      problems.push({ row: i + 1, col: j + 1, raw, why: v.why });
      return null;
    });
    while (out.length < width) out.push(null);
    return out;
  });
  if (problems.length > 0) {
    const shown = problems.slice(0, 5)
      .map((p) => `row ${p.row}, column ${p.col}: “${p.raw.length > 20 ? `${p.raw.slice(0, 20)}…` : p.raw}” — ${p.why}`);
    const more = problems.length > 5 ? ` (and ${problems.length - 5} more)` : '';
    return {
      ok: false,
      problems,
      message: `Nothing was pasted — the clipboard has ${problems.length} value${problems.length === 1 ? '' : 's'} that aren’t crew counts: ${shown.join('; ')}${more}.`,
    };
  }
  return { ok: true, matrix };
}

/**
 * A block of crew counts → tab-separated text, for Ctrl+C. Zero is written as
 * an empty cell because that is how the grid shows it; it pastes back as 0.
 *
 * ⚠️ Every row ENDS with CRLF, as Excel writes it. parseClipboard drops one
 * trailing newline; without this one, a block whose last row is a single
 * empty cell lost that row on the way back — and a 2×1 block became 1×1,
 * which pastes as a fill over the whole selection.
 */
export function toClipboardText(matrix: number[][]): string {
  return matrix.map((r) => `${r.map((v) => (v === 0 ? '' : String(v))).join('\t')}\r\n`).join('');
}
